import React, { useEffect, useState } from 'react';
import { Users, Plus, Building2, MapPin, Mail, Phone, Lock, RefreshCw } from 'lucide-react';
import type { Client } from '@/types/client';
import { ClientTypeBadge } from '@/components/clients';
import api from '@/services/api';
import { useAlert } from '../../components/AlertProvider';
import { AddButton } from '@/components/ui/AddButton';
import OpenStreetMapAddressPicker from '../../components/ui/GoogleMapsAddressPicker';
import { sanitizePhoneInput } from '@/utils/phoneUtils';

const MyMemberBuyers: React.FC = () => {
  const { showSuccess, showError } = useAlert();
  const [members, setMembers] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    maps_link: '',
    siret: '',
    password: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients/me/member-buyers');
      setMembers(response.data);
    } catch (error: any) {
      console.error('Erreur chargement clients membres:', error);
      showError('Impossible de charger les clients membres');
    } finally {
      setLoading(false);
    }
  };

  const regenerateBPU = async (memberId: number) => {
    try {
      const response = await api.post(`/clients/me/member-buyers/${memberId}/regenerate-bpu`);
      showSuccess(response.data.message || 'BPU régénéré avec succès');
      await loadMembers(); // Recharger la liste
    } catch (error: any) {
      console.error('Erreur régénération BPU:', error);
      showError(error.response?.data?.detail || 'Erreur lors de la régénération du BPU');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.company_name || !formData.email || !formData.password) {
      showError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/clients/me/member-buyers', formData);

      // Réinitialiser le formulaire
      setFormData({
        company_name: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'France',
        maps_link: '',
        siret: '',
        password: '',
      });

      setIsModalOpen(false);
      loadMembers();

      // Success message
      showSuccess('Client membre créé avec succès');
    } catch (error: any) {
      console.error('Erreur création client membre:', error);
      showError(error.response?.data?.detail || 'Erreur lors de la création du client membre');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Mes Clients Membres</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Gérez les clients rattachés à votre centrale d'achat
            </p>
          </div>
        </div>
        <AddButton
          onClick={() => setIsModalOpen(true)}
          label="Nouveau"
          className="w-full sm:w-auto"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-700">
            <Building2 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Aucun client contact
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              Commencez par créer votre premier client contact
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200"
            >
              <Plus className="h-5 w-5" />
              <span>Créer un client</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member) => (
              <div
                key={member.id}
                className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-800 hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                      <Building2 className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                        {member.company_name}
                      </h3>
                      <ClientTypeBadge type={member.client_type} variant="compact" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {(member.first_name || member.last_name) && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Users className="h-4 w-4" />
                      <span>{`${member.first_name ?? ''} ${member.last_name ?? ''}`.trim()}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{member.email}</span>
                  </div>
                  {member.phone && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Phone className="h-4 w-4" />
                      <span>{member.phone}</span>
                    </div>
                  )}
                  {member.city && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <MapPin className="h-4 w-4" />
                      <span>{member.city}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                  {member.bpu_locked ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                        <Lock className="h-3 w-3" />
                        <span>BPU hérité (verrouillé)</span>
                      </div>
                      <button
                        onClick={() => regenerateBPU(member.id)}
                        className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                        title="Régénérer le BPU hérité"
                      >
                        <RefreshCw className="h-3 w-3" />
                        <span>Régénérer</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => regenerateBPU(member.id)}
                      className="w-full text-xs px-3 py-2 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded hover:bg-cyan-200 dark:hover:bg-cyan-900/50 transition-colors flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="h-3 w-3" />
                      <span>Créer le BPU hérité</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Création */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Nouveau Client contact</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Les champs marqués d'un * sont obligatoires</p>
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">Entreprise *</label>
                  <input
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Prénom *</label>
                  <input
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nom *</label>
                  <input
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mot de passe *</label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Téléphone</label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: sanitizePhoneInput(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">SIRET</label>
                  <input
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    placeholder="Ex: 123 456 789 00012"
                  />
                </div>
                <div className="sm:col-span-2">
                  <OpenStreetMapAddressPicker
                    onAddressSelect={(addressData: any) => {
                      setFormData({
                        ...formData,
                        address: addressData.address,
                        city: addressData.city,
                        postal_code: addressData.postal_code,
                        country: addressData.country,
                        maps_link: addressData.maps_link
                      });
                    }}
                    initialAddress={formData.address}
                    initialCity={formData.city}
                    initialPostalCode={formData.postal_code}
                    initialCountry={formData.country}
                    initialMapsLink={formData.maps_link}
                  />
                </div>
              </div>

              {/* Info BPU */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  ℹ️ Le BPU sera automatiquement hérité de la centrale et ne pourra pas être modifié manuellement.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!formData.company_name.trim() || !formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim() || !formData.password.trim() || submitting}
                >
                  {submitting ? 'Création...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMemberBuyers;
